import React, { useEffect, useState, useCallback } from 'react';
import {
    AppBar,
    Toolbar,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Snackbar,
    Alert,
    Box,
    CircularProgress,
    Tabs,
    Tab
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { getFacilities, addFacility, getSlicOptions, getCenterOptions, getUldTypeOptions, getSiteOptions } from './facilityService';
import Autocomplete from '@mui/material/Autocomplete';
import AssociationTab from './AssociationTab'; // Tab for activeTab === 1

const Dashboard = ({ userEmail, onLogout }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [facilities, setFacilities] = useState([]);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [totalRecords, setTotalRecords] = useState(0);
    const [open, setOpen] = useState(false);
    const [newRecord, setNewRecord] = useState({ id: '', slic: '', center: '', uld_type: '', site: '' });
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [idError, setIdError] = useState('');

    const [slicOptions, setSlicOptions] = useState([]);
    const [centerOptions, setCenterOptions] = useState([]);
    const [uldTypeOptions, setUldTypeOptions] = useState([]);
    const [siteOptions, setSiteOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [filters, setFilters] = useState({ id: '', slic: '', center: '', uld_type: '', site: '' });

    const fetchFacilities = useCallback(async (page, pageSize, filters = {}) => {
        try {
            const { data, pagination } = await getFacilities(page, pageSize, filters);
            setFacilities(data);
            setTotalRecords(pagination.totalRecords);
        } catch (error) {
            console.error('Error fetching facilities:', error);
            setErrorMessage('Failed to fetch data.');
        }
    }, []);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    }

    useEffect(() => {
        fetchFacilities(paginationModel.page + 1, paginationModel.pageSize, filters);
    }, [paginationModel, fetchFacilities, filters]);

    useEffect(() => {
        const fetchDropdownOptions = async () => {
            setLoadingOptions(true);
            try {
                const [slicData, centerData, uldTypeData, siteData] = await Promise.all([
                    getSlicOptions(),
                    getCenterOptions(),
                    getUldTypeOptions(),
                    getSiteOptions(),
                ]);
                setSlicOptions(slicData);
                setCenterOptions(centerData);
                setUldTypeOptions(uldTypeData);
                setSiteOptions(siteData);
            } catch (error) {
                console.error('Error fetching dropdown options:', error);
                setErrorMessage('Failed to fetch dropdown options.');
            } finally {
                setLoadingOptions(false);
            }
        };
        fetchDropdownOptions();
    }, []);

    const handlePaginationChange = (model) => setPaginationModel(model);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setNewRecord({ id: '', slic: '', center: '', uld_type: '', site: '' });
        setIdError('');
    };

    const validateId = (id) => {
        const regex = /^UPSP\d{5,6}$/;
        return regex.test(id);
    };

    const handleIdChange = (event) => {
        const { value } = event.target;
        const uppercaseValue = value.toUpperCase();

        if (!validateId(uppercaseValue)) {
            setIdError('ID should be in the format UPSP followed by 5 or 6 digits');
        } else {
            setIdError('');
        }

        setNewRecord({ ...newRecord, id: uppercaseValue });
    };

    const handleFormSubmit = async () => {
        const errors = {
            slic: !newRecord.slic,
            center: !newRecord.center,
            uld_type: !newRecord.uld_type,
            site: !newRecord.site,
        };
        if (Object.values(errors).some((error) => error || idError)) {
            setErrorMessage(idError || 'Please fill in all required fields');
            return;
        }

        try {
            const recordWithUser = { ...newRecord, userEmail };
            await addFacility(recordWithUser);
            setSuccessMessage('Record added successfully!');
            handleClose();
            setPaginationModel({ ...paginationModel, page: 0 });
        } catch (error) {
            console.error('Error in handleFormSubmit:', error);
            if (error.response && error.response.status === 409) {
                setErrorMessage('A record with the ID already exists.');
            } else {
                setErrorMessage('Failed to add data. Please try again.');
            }
        }
    };

    const handleCloseSnackbar = () => {
        setSuccessMessage('');
        setErrorMessage('');
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 150 },
        { field: 'slic', headerName: 'SLIC', width: 150 },
        { field: 'center', headerName: 'Center', width: 150 },
        { field: 'uld_type', headerName: 'ULD Type', width: 150 },
        { field: 'site', headerName: 'Site', width: 150 },
    ];

    return (
        <Box sx={{ width: '100vw', minHeight: '100vh', backgroundColor: '#121212', display: 'flex', flexDirection: 'column' }}>
            <AppBar position="static" sx={{ background: 'linear-gradient(to right, #333333, #1e1e1e)' }}>
                <Toolbar>
                    <Box component="img" sx={{ height: 40, marginRight: 'auto' }} alt="Trackonomy Logo" src="/logo.png" />
                    <Tabs value={activeTab} onChange={handleTabChange} textColor="inherit" indicatorColor="primary">
                        <Tab label="Facility" />
                        <Tab label="Change Association" />
                    </Tabs>
                    <Button
                        variant="contained"
                        onClick={handleClickOpen}
                        sx={{ backgroundColor: '#ff5722', color: '#ffffff', marginRight: '20px' }}
                    >
                        Add Record
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={onLogout}
                        sx={{ color: '#e0e0e0', borderColor: '#e0e0e0' }}
                    >
                        Sign Out
                    </Button>
                </Toolbar>
            </AppBar>
            {/* Global Search mode  */}
            {activeTab === 0 && (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, padding: 2 }}>
                        <TextField
                            label="Filter by ID"
                            variant="outlined"
                            value={filters.id}
                            onChange={(e) => handleFilterChange('id', e.target.value)}
                            sx={{ width: '200px' }}
                        />
                        <Autocomplete
                            options={slicOptions}
                            value={filters.slic}
                            onChange={(e, newValue) => handleFilterChange('slic', newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Filter by SLIC" variant="outlined" sx={{ width: '200px' }} />
                            )}
                        />
                        <Autocomplete
                            options={centerOptions}
                            value={filters.center}
                            onChange={(e, newValue) => handleFilterChange('center', newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Filter by Center" variant="outlined" sx={{ width: '200px' }} />
                            )}
                        />
                        <Autocomplete
                            options={uldTypeOptions}
                            value={filters.uld_type}
                            onChange={(e, newValue) => handleFilterChange('uld_type', newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Filter by ULD Type" variant="outlined" sx={{ width: '200px' }} />
                            )}
                        />
                        <Autocomplete
                            options={siteOptions}
                            value={filters.site}
                            onChange={(e, newValue) => handleFilterChange('site', newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Filter by Site" variant="outlined" sx={{ width: '200px' }} />
                            )}
                        />
                    </Box>

                    <Box sx={{ flexGrow: 1, p: 2 }}>
                        <DataGrid
                            rows={facilities}
                            columns={columns}
                            pagination
                            paginationMode="server"
                            rowCount={totalRecords}
                            pageSizeOptions={[10, 20, 50]}
                            paginationModel={paginationModel}
                            onPaginationModelChange={handlePaginationChange}
                            disableSelectionOnClick
                        />
                    </Box>
                </>
            )}
            {activeTab === 1 && (
                <AssociationTab />
            )}
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Add New Facility Record</DialogTitle>
                <DialogContent>
                    <DialogContentText>Enter the details for the new facility record.</DialogContentText>
                    <TextField
                        label="ID"
                        fullWidth
                        value={newRecord.id}
                        onChange={handleIdChange}
                        error={!!idError}
                        helperText={idError}
                    />
                    <Autocomplete
                        options={slicOptions}
                        value={newRecord.slic}
                        onChange={(e, newValue) => setNewRecord((prev) => ({ ...prev, slic: newValue }))}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="SLIC"
                                fullWidth
                                margin="dense"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingOptions ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <Autocomplete
                        options={centerOptions}
                        value={newRecord.center}
                        onChange={(e, newValue) => setNewRecord((prev) => ({ ...prev, center: newValue }))}
                        renderInput={(params) => (
                            <TextField {...params} label="Center" fullWidth margin="dense" />
                        )}
                    />
                    <Autocomplete
                        options={uldTypeOptions}
                        value={newRecord.uld_type}
                        onChange={(e, newValue) => setNewRecord((prev) => ({ ...prev, uld_type: newValue }))}
                        renderInput={(params) => (
                            <TextField {...params} label="ULD Type" fullWidth margin="dense" />
                        )}
                    />
                    <Autocomplete
                        options={siteOptions}
                        value={newRecord.site}
                        onChange={(e, newValue) => setNewRecord((prev) => ({ ...prev, site: newValue }))}
                        renderInput={(params) => (
                            <TextField {...params} label="Site" fullWidth margin="dense" />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleFormSubmit} color="primary">
                        Add
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!successMessage} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <Alert severity="success">{successMessage}</Alert>
            </Snackbar>
            <Snackbar open={!!errorMessage} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <Alert severity="error">{errorMessage}</Alert>
            </Snackbar>
        </Box>
    );
};

export default Dashboard;
